import { useEffect, useRef } from 'react'
import type { JSX } from 'react'

import {
  Badge,
  Card,
  CriticalIcon,
  EmptyState,
  Flex,
  Heading,
  LockIcon,
  Notice,
  SearchIcon,
  Skeleton,
  toast,
  UserIcon,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import {
  useAccount,
  useOrganizationId,
  useHasOrganizationAction,
  useAccountId,
} from '~publish/contexts/AccountContext'

import { EmailVerificationBanner } from '~publish/pages/Settings/Profile/components/Email'
import { PendingInvitationRow, TeamMemberRow } from './TeamMemberRow'
import {
  usePendingInvitations,
  useResendInvitation,
  useRevokeInvitation,
  useTeamMembers,
} from '../../hooks'
import type { PendingInvitation, TeamMember } from '../../hooks'

import styles from './TeamMembersList.module.css'

interface TeamMembersListProps {
  /**
   * Active member search term (already debounced upstream). Filters the list
   * server-side by name/email. Empty string means no search is applied.
   */
  search?: string
  onEdit: (member: TeamMember) => void
  onRemove: (member: TeamMember) => void
}

export const TeamMembersList = ({
  search = '',
  onEdit,
  onRemove,
}: TeamMembersListProps): JSX.Element => {
  const { account } = useAccount()
  const organizationId = useOrganizationId()
  const canManageTeamMembers = useHasOrganizationAction('manageTeamMembers')
  const accountId = useAccountId()
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const {
    teamMembers,
    loading,
    loadingMore,
    error,
    errorCode,
    hasNextPage,
    loadMore,
  } = useTeamMembers({
    organizationId: organizationId ?? '',
    search,
  })

  const isSearching = search.trim() !== ''

  const { pendingInvitations } = usePendingInvitations({
    organizationId: organizationId ?? '',
    enabled: canManageTeamMembers,
  })

  const { revokeInvitation } = useRevokeInvitation()
  const { resendInvitation } = useResendInvitation()

  const handleRevokeInvitation = async (
    invitation: PendingInvitation,
  ): Promise<void> => {
    if (!organizationId) return
    const result = await revokeInvitation({
      organizationId,
      invitationId: invitation.id,
    })
    if (result.success) {
      toast.success(`Invitation to ${invitation.email} revoked`)
    } else {
      toast.error(result.error ?? 'Failed to revoke invitation')
    }
  }

  const handleResendInvitation = async (
    invitation: PendingInvitation,
  ): Promise<void> => {
    if (!organizationId) return
    const result = await resendInvitation({
      organizationId,
      invitationId: invitation.id,
    })
    if (result.success) {
      toast.success(`Invitation to ${invitation.email} resent`)
    } else {
      toast.error(result.error ?? 'Failed to resend invitation')
    }
  }

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    const element = loadMoreRef.current
    if (!element || !hasNextPage || loadingMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { threshold: 0.1 },
    )

    observer.observe(element)

    return (): void => {
      observer.disconnect()
    }
  }, [hasNextPage, loadingMore, loadMore])

  // Initial loading state
  if (!organizationId || (loading && !teamMembers)) {
    return (
      <>
        {[0, 1, 2].map((index) => (
          <Card
            key={index}
            className={styles.memberRow}
            data-testid="member-skeleton"
          >
            <Skeleton rounded width={40} height={40} />
            <Flex direction="column" gap="2xs" className={styles.memberInfo}>
              <Skeleton width="140px" height="16px" />
              <Skeleton width="200px" height="14px" />
            </Flex>
          </Card>
        ))}
      </>
    )
  }

  // Email verification gate
  if (!account?.hasVerifiedEmail) {
    return (
      <EmailVerificationBanner>
        <Notice.Text>
          To manage team members, please verify your email address. An email has
          been sent to your inbox to verify your email address.
        </Notice.Text>
      </EmailVerificationBanner>
    )
  }

  // Error state
  if (error) {
    if (errorCode === 'FORBIDDEN') {
      return (
        <EmptyState variant="neutral">
          <EmptyState.Icon>
            <LockIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>
            You don&apos;t have permission to view team members
          </EmptyState.Heading>
          <EmptyState.Description>
            Only administrators can manage the team. If you just joined, you may
            need to verify your email address first.
          </EmptyState.Description>
        </EmptyState>
      )
    }

    return (
      <EmptyState variant="critical">
        <EmptyState.Icon>
          <CriticalIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Failed to load team members</EmptyState.Heading>
        <EmptyState.Description>
          Something went wrong. Please try again later.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  // While searching, focus the view on matching members only — the search box
  // is labelled "Search members", so keeping the (unfiltered) pending-invites
  // section visible would be confusing.
  const showPending = pendingInvitations.length > 0 && !isSearching

  // No members matched the active search. Distinct from the "no team members
  // at all" empty state below so we don't prompt the user to invite when they
  // were just filtering.
  if (isSearching && (!teamMembers || teamMembers.length === 0)) {
    return (
      <EmptyState variant="neutral" size="xlarge" className={styles.emptyState}>
        <EmptyState.Icon>
          <SearchIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>No members found</EmptyState.Heading>
        <EmptyState.Description>
          Try adjusting your search terms.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  // Empty state — no active members and no pending invites either.
  if ((!teamMembers || teamMembers.length === 0) && !showPending) {
    return (
      <EmptyState variant="neutral" size="xlarge" className={styles.emptyState}>
        <EmptyState.Icon>
          <UserIcon />
        </EmptyState.Icon>
        <VisuallyHidden>
          <EmptyState.Heading>No team members</EmptyState.Heading>
        </VisuallyHidden>
        <EmptyState.Description>
          Invite team members to collaborate on your social media content.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  const regularMembers = teamMembers ?? []

  return (
    <>
      {showPending && (
        <Flex direction="column" gap="sm" className={styles.section}>
          <Flex align="center" gap="xs" className={styles.pendingHeader}>
            <Heading as="h2" size="sm">
              Pending Invites
            </Heading>
            <Badge size="small" color="warning">
              {pendingInvitations.length}
            </Badge>
          </Flex>
          {pendingInvitations.map((invitation) => (
            <PendingInvitationRow
              key={invitation.id}
              invitation={invitation}
              onRevoke={handleRevokeInvitation}
              onResend={handleResendInvitation}
              canManage={canManageTeamMembers}
            />
          ))}
        </Flex>
      )}

      {regularMembers.length > 0 && (
        <Flex direction="column" gap="sm" className={styles.section}>
          {showPending && (
            <Heading as="h2" size="sm">
              Team
            </Heading>
          )}
          {regularMembers.map((member) => (
            <TeamMemberRow
              key={member.id}
              teamMember={member}
              onEdit={onEdit}
              onRemove={onRemove}
              canManage={canManageTeamMembers}
              isCurrentUser={member.id === accountId}
            />
          ))}
        </Flex>
      )}

      {/* Load more trigger element */}
      {hasNextPage && (
        <div ref={loadMoreRef} className={styles.loadMoreTrigger}>
          {loadingMore && <Skeleton width="100%" height="74px" />}
        </div>
      )}
    </>
  )
}
